import { FaChartLine, FaPalette, FaVideo, FaCode, FaBullhorn, FaUser } from 'react-icons/fa';

export const servicesData = [
  { 
    id: 'social-media-management',
    icon: FaChartLine,
    title: 'Social Media Management',
    description: 'Strategic content creation, community engagement, and data-driven growth to build a powerful online presence.',
    features: ['Content Strategy', 'Community Building', 'Analytics & Reporting', 'Scheduling & Automation', 'Influencer Outreach', 'Hashtag Research'],
    gradient: 'from-cyan-400 to-blue-500',
    tagline: 'Turn Followers into Loyal Customers',
    details: 'Our Social Media Management service goes beyond posting content. We dive deep into your target audience to create a tailored strategy that amplifies your voice. Through continuous A/B testing, rapid response community management, and high-impact visual design, we transform followers into loyal brand advocates. We handle everything from daily posting schedules to influencer partnerships, ensuring your brand stays top-of-mind 24/7.',
    whyItMatters: 'Social media is no longer optional — it is where your customers live. Brands that show up consistently, authentically, and creatively win market share. We ensure your social presence commands attention and builds lasting trust with your audience at every stage of the funnel.',
    process: [
      { step: '01', title: 'Audience & Competitor Research', desc: 'We analyse your ideal customer profiles and benchmark against top competitors to identify content gaps and winning angles.' },
      { step: '02', title: 'Strategy & Content Calendar', desc: 'We build a month-by-month content roadmap tailored to your brand voice, campaign goals, and platform algorithms.' },
      { step: '03', title: 'Content Creation & Scheduling', desc: 'Our in-house creators produce eye-catching graphics, reels, and captions. We schedule everything at peak engagement windows.' },
      { step: '04', title: 'Community Management', desc: 'We respond to comments, DMs, and mentions daily — turning every interaction into a brand-building touchpoint.' },
      { step: '05', title: 'Monthly Reporting & Optimisation', desc: 'Detailed analytics reports every month with key insights and strategy adjustments to continuously improve performance.' },
    ],
    stats: [
      { value: '3x', label: 'Average Follower Growth' },
      { value: '87%', label: 'Increase in Engagement' },
      { value: '40+', label: 'Platforms Managed' },
      { value: '24/7', label: 'Community Monitoring' },
    ],
    platforms: ['Instagram', 'Facebook', 'LinkedIn', 'TikTok', 'X (Twitter)', 'YouTube', 'Pinterest', 'Threads'],
    pricing: [
      {
        tier: 'Starter', price: 'X,XXX', period: '/month',
        description: 'Perfect for small businesses looking to establish a presence.',
        features: ['2 Social Platforms', '12 Posts/month', 'Basic Community Management', 'Monthly Report'],
        isPopular: false
      },
      {
        tier: 'Growth', price: 'XX,XXX', period: '/month',
        description: 'Comprehensive management for growing brands.',
        features: ['4 Social Platforms', '20 Posts/month', '4 Custom Reels/month', 'Proactive Engagement', 'Bi-weekly Strategy Call'],
        isPopular: true
      },
      {
        tier: 'Scale', price: 'XX,XXX', period: '/month',
        description: 'Aggressive growth and omni-channel dominance.',
        features: ['All Platforms', 'Daily Posting', '8 Custom Reels/month', 'Influencer Outreach', 'Dedicated Account Manager'],
        isPopular: false
      }
    ]
  },
  { 
    id: 'graphic-design-branding',
    icon: FaPalette,
    title: 'Graphic Design & Branding',
    description: 'Create unforgettable brand identities that resonate deeply with your target audience and stand out.',
    features: ['Brand Identity', 'Visual Design', 'Brand Guidelines', 'Logo Design', 'Marketing Collateral', 'Packaging Design'],
    gradient: 'from-purple-400 to-pink-500',
    tagline: 'Design That Speaks Before You Do',
    details: 'A strong brand is your company\'s most valuable asset. Our elite design team creates comprehensive brand identities that capture the essence of your business. We deliver stunning visual assets including logos, typography systems, color palettes, and full brand guidelines. Every pixel is crafted to evoke emotion and establish trust, positioning you as the premium choice in your market.',
    whyItMatters: 'People form a first impression of your brand in just 50 milliseconds. Bad design loses customers before you even get a chance to speak. Great design communicates quality, trustworthiness, and professionalism instantly — giving you an edge over every competitor who neglects it.',
    process: [
      { step: '01', title: 'Brand Discovery Workshop', desc: 'We hold a deep-dive session to understand your mission, values, audience, and competitive landscape before a single pixel is designed.' },
      { step: '02', title: 'Moodboard & Direction', desc: 'We present 2–3 visual directions — each with its own tone, typography, and colour story — so you can choose the path that resonates most.' },
      { step: '03', title: 'Logo & Identity Design', desc: 'Our designers craft your primary logo, secondary marks, typography pairings, and a curated colour system built for digital and print.' },
      { step: '04', title: 'Brand Guidelines Document', desc: 'A comprehensive bible for your brand — covering logo usage rules, typography, colours, photography style, and voice guidelines.' },
      { step: '05', title: 'Collateral & Rollout', desc: 'We apply your new identity across all marketing materials — social templates, business cards, presentations, and packaging.' },
    ],
    stats: [
      { value: '500+', label: 'Brands Designed' },
      { value: '100%', label: 'Client Satisfaction Rate' },
      { value: '48hrs', label: 'First Concept Delivery' },
      { value: '12+', label: 'Industries Served' },
    ],
    platforms: ['Social Media Templates', 'Print Design', 'Digital Ads', 'Packaging', 'Presentations', 'Brand Books', 'Merchandise', 'Signage'],
    pricing: [
      {
        tier: 'Essential', price: 'X,XXX', period: '/project',
        description: 'Core branding elements to get your business started.',
        features: ['Primary Logo Design', 'Brand Color Palette', 'Typography Selection', '2 Revisions'],
        isPopular: false
      },
      {
        tier: 'Professional', price: 'XX,XXX', period: '/project',
        description: 'Complete brand identity system for established businesses.',
        features: ['Full Logo Suite', 'Comprehensive Brand Guidelines', 'Business Card Design', 'Social Media Templates', 'Unlimited Revisions'],
        isPopular: true
      },
      {
        tier: 'Premium', price: 'XX,XXX', period: '/project',
        description: 'Full-scale brand transformation and collateral rollout.',
        features: ['Everything in Professional', 'Pitch Deck Design', 'Packaging Design', 'Brand Strategy Workshop', 'Priority Delivery'],
        isPopular: false
      }
    ]
  },
  { 
    id: 'video-production',
    icon: FaVideo,
    title: 'Video Production & Editing',
    description: 'Professional video content that tells compelling stories, engages viewers, and drives conversions.',
    features: ['Script Writing', 'Full Production', 'Post-Production', 'Motion Graphics', 'Colour Grading', 'Sound Design'],
    gradient: 'from-orange-400 to-red-500',
    tagline: 'Stories That Stop the Scroll',
    details: 'Video is the highest-converting medium on the internet. Our in-house production team handles the entire lifecycle of your video content. From ideation and scriptwriting to cinematic shooting and high-end post-production. Whether it\'s a 15-second TikTok ad, a corporate documentary, or an immersive product showcase, we deliver visuals that demand attention and drive action.',
    whyItMatters: 'Video content generates 1200% more shares than text and images combined. Websites with video see an 80% increase in conversion rates. Brands that invest in consistent video content establish authority faster, drive more traffic, and close customers at a dramatically higher rate than those that don\'t.',
    process: [
      { step: '01', title: 'Creative Brief & Concept', desc: 'We start by understanding your goal — brand awareness, lead generation, or sales — and build a creative concept around your target message.' },
      { step: '02', title: 'Script Writing & Storyboard', desc: 'Our copywriters craft a compelling script. We then create a full storyboard so you can visualise the video before production begins.' },
      { step: '03', title: 'Pre-Production Planning', desc: 'Logistics, casting (if needed), location scouting, equipment planning — we handle all the behind-the-scenes preparation.' },
      { step: '04', title: 'Production (Filming)', desc: 'Our professional crew shoots your video using cinema-grade cameras, professional lighting rigs, and premium audio equipment.' },
      { step: '05', title: 'Post-Production & Delivery', desc: 'Expert editing, motion graphics, colour grading, sound design, and subtitle creation. Delivered in all required formats and aspect ratios.' },
    ],
    stats: [
      { value: '1200%', label: 'More Shares than Text' },
      { value: '80%', label: 'Conversion Rate Increase' },
      { value: '200+', label: 'Videos Produced' },
      { value: '4K', label: 'Cinema-Grade Quality' },
    ],
    platforms: ['YouTube', 'Instagram Reels', 'TikTok', 'LinkedIn', 'Website Hero Videos', 'Ads', 'Explainer Videos', 'Corporate Films'],
    pricing: [
      {
        tier: 'Social Shorts', price: 'X,XXX', period: '/month',
        description: 'High-performing short-form content for social media.',
        features: ['8 Reels/TikToks per month', 'Scripting & Ideation', 'Basic Motion Graphics', 'Trending Audio Integration'],
        isPopular: false
      },
      {
        tier: 'Brand Story', price: 'XX,XXX', period: '/project',
        description: 'Cinematic brand films to establish authority.',
        features: ['1 Hero Brand Film (2 mins)', 'Full Day Shoot', 'Professional Lighting & Audio', 'Color Grading', '15-sec Cutdowns'],
        isPopular: true
      },
      {
        tier: 'Ad Campaigns', price: 'XX,XXX', period: '/project',
        description: 'High-converting video ads engineered for paid media.',
        features: ['3 Unique Ad Concepts', 'A/B Testing Variations', 'Advanced Motion Graphics', 'Professional Voiceover', 'Commercial Licensing'],
        isPopular: false
      }
    ]
  },
  { 
    id: 'web-development',
    icon: FaCode,
    title: 'Web Development & SEO',
    description: 'Lightning-fast, responsive websites optimized for search engines and designed for maximum conversions.',
    features: ['Custom Development', 'SEO Optimisation', 'Performance Tuning', 'UX/UI Design', 'E-Commerce Solutions', 'Analytics & Tracking'],
    gradient: 'from-green-400 to-emerald-500',
    tagline: 'Websites That Rank, Convert & Scale',
    details: 'Your website is your digital storefront. We build blazing-fast, technically flawless web experiences using modern frameworks like React and Next.js. Coupled with our aggressive SEO strategies, we ensure your site not only looks breathtaking but ranks at the top of Google. We focus on Core Web Vitals, seamless UX, and conversion rate optimisation to turn traffic into revenue.',
    whyItMatters: '75% of users judge a company\'s credibility based on its website design. A 1-second delay in page load time causes a 7% reduction in conversions. A powerful website paired with strong SEO is the highest-ROI digital investment any business can make — generating compounding organic traffic 24/7.',
    process: [
      { step: '01', title: 'Discovery & Sitemap Planning', desc: 'We map out your site architecture, user journeys, and conversion goals. Every page has a purpose aligned with your business objectives.' },
      { step: '02', title: 'UX/UI Design', desc: 'High-fidelity mockups are designed for every key page — crafted to be visually stunning while guiding visitors toward conversion actions.' },
      { step: '03', title: 'Development & Build', desc: 'We build using modern, scalable code (React, Next.js, headless CMS). Mobile-first, pixel-perfect, and performance-optimised from day one.' },
      { step: '04', title: 'SEO Implementation', desc: 'On-page SEO, technical SEO, schema markup, speed optimisation, and keyword-targeted content are baked into every page of the build.' },
      { step: '05', title: 'Launch, Monitor & Grow', desc: 'Post-launch, we set up full analytics tracking and continue an ongoing SEO strategy to rank your site higher every single month.' },
    ],
    stats: [
      { value: '<1s', label: 'Average Load Time' },
      { value: '100', label: 'Google PageSpeed Score' },
      { value: '5x', label: 'Organic Traffic Growth' },
      { value: '150+', label: 'Sites Launched' },
    ],
    platforms: ['React / Next.js', 'WordPress / Headless', 'Shopify / E-Commerce', 'Webflow', 'Custom CMS', 'Landing Pages', 'Web Apps', 'Portfolios'],
    pricing: [
      {
        tier: 'Landing Page', price: 'X,XXX', period: '/project',
        description: 'High-converting single page for lead generation.',
        features: ['Custom UI/UX Design', 'Mobile Responsive', 'Lead Capture Integration', 'Basic On-page SEO'],
        isPopular: false
      },
      {
        tier: 'Corporate Site', price: 'XX,XXX', period: '/project',
        description: 'Full multi-page website to establish your digital presence.',
        features: ['Up to 10 Custom Pages', 'CMS Integration', 'Advanced SEO Setup', 'Speed Optimization', 'Analytics Integration'],
        isPopular: true
      },
      {
        tier: 'E-Commerce / Custom App', price: 'XX,XXX', period: '/project',
        description: 'Complex web applications and scalable storefronts.',
        features: ['Custom React/Next.js Build', 'Payment Gateway Integration', 'Advanced Database Architecture', 'Ongoing Tech Support'],
        isPopular: false
      }
    ]
  },
  { 
    id: 'paid-advertising',
    icon: FaBullhorn,
    title: 'Paid Advertising',
    description: 'Data-driven ad campaigns across all platforms engineered to maximize ROI and scale your business.',
    features: ['Google Ads', 'Meta Ads', 'Retargeting Funnels', 'Ad Creative Design', 'A/B Split Testing', 'ROAS Optimisation'],
    gradient: 'from-yellow-400 to-orange-500',
    tagline: 'Every Rupee Working Harder for You',
    details: 'Stop wasting ad spend. Our performance marketing experts construct hyper-targeted campaigns across Google, Meta, LinkedIn, and TikTok. We utilise advanced pixel tracking, dynamic retargeting, and rigorous split-testing to discover the most profitable funnels. Our goal is simple: maximise your Return on Ad Spend (ROAS) and scale your customer acquisition profitably.',
    whyItMatters: 'Organic reach is declining. Paid advertising, when managed strategically, is the fastest lever for predictable revenue growth. The difference between a profitable campaign and a money-burning one is the expertise of who is managing it. Our team has managed crores in ad spend and knows exactly how to make every rupee count.',
    process: [
      { step: '01', title: 'Account Audit & Setup', desc: 'We audit existing ad accounts (or build new ones) to eliminate waste, fix tracking issues, and establish a clean, optimised foundation.' },
      { step: '02', title: 'Audience & Keyword Research', desc: 'Deep research into your ideal customer segments, competitor targeting strategies, and the highest-intent keywords to target.' },
      { step: '03', title: 'Ad Creative & Copywriting', desc: 'We design high-converting ad visuals and write compelling copy for every stage of your funnel — awareness, consideration, and conversion.' },
      { step: '04', title: 'Campaign Launch & Monitoring', desc: 'Campaigns are launched with strict budget controls. We monitor performance daily, killing losers and scaling winners in real-time.' },
      { step: '05', title: 'Weekly Reporting & Scaling', desc: 'Transparent weekly reports showing spend, conversions, ROAS, and CPL. We scale budgets strategically as performance is proven.' },
    ],
    stats: [
      { value: '4.8x', label: 'Average ROAS Delivered' },
      { value: '60%', label: 'Reduction in Cost Per Lead' },
      { value: '₹10Cr+', label: 'Ad Spend Managed' },
      { value: '30+', label: 'Campaigns Running' },
    ],
    platforms: ['Google Search & Display', 'Meta (Facebook & Instagram)', 'LinkedIn Ads', 'TikTok Ads', 'YouTube Ads', 'Programmatic Display', 'Retargeting', 'Shopping Ads'],
    pricing: [
      {
        tier: 'Growth', price: 'X,XXX', period: '/month',
        description: 'For businesses spending up to ₹1,00,000/mo in ads.',
        features: ['1 Ad Platform (Meta OR Google)', 'Ad Creative Production', 'Weekly Optimizations', 'Monthly Reporting'],
        isPopular: false
      },
      {
        tier: 'Scaling', price: 'XX,XXX', period: '/month',
        description: 'For businesses spending up to ₹5,000,000/mo in ads.',
        features: ['2 Ad Platforms', 'Advanced Retargeting Funnels', 'A/B Testing Creatives', 'Bi-weekly Strategy Calls'],
        isPopular: true
      },
      {
        tier: 'Enterprise', price: 'XX,XXX', period: '/month',
        description: 'Omni-channel scaling for large ad budgets.',
        features: ['All Ad Platforms', 'Custom Landing Pages included', 'Daily Monitoring & Scaling', 'Dedicated Growth Partner'],
        isPopular: false
      }
    ]
  },
  { 
    id: 'personal-brand-management',
    icon: FaUser,
    title: 'Personal Brand Management',
    description: 'Comprehensive digital presence management for professionals, executives, and influencers.',
    features: ['Reputation Management', 'Content Strategy', 'LinkedIn Growth', 'Thought Leadership', 'Media Outreach', 'Online PR'],
    gradient: 'from-indigo-400 to-purple-500',
    tagline: 'You Are the Brand. We Make the World Know It.',
    details: 'In today\'s economy, your personal brand is your reputation. We act as your digital PR and growth team, crafting compelling thought leadership content and managing your LinkedIn and X (Twitter) presence. We secure podcast appearances and editorial features. We build your authority and influence so opportunities come to you effortlessly.',
    whyItMatters: 'People buy from people. A CEO with a strong personal brand generates up to 7x more leads for their company. In a world of noise, the person who is most visible and most trusted wins. Your personal brand works for you while you sleep — attracting clients, talent, and opportunities around the clock.',
    process: [
      { step: '01', title: 'Personal Brand Audit', desc: 'We audit your existing digital presence across all platforms and identify your authority gaps, perception issues, and growth opportunities.' },
      { step: '02', title: 'Positioning & Messaging', desc: 'We define your unique positioning statement, your niche authority, and the core messages that will define your public voice.' },
      { step: '03', title: 'Content Production', desc: 'Our ghostwriting team produces LinkedIn posts, long-form articles, X threads, and newsletter content that sounds authentically like you.' },
      { step: '04', title: 'Media & Podcast Outreach', desc: 'We pitch you to relevant podcasts, industry publications, and media outlets to build your authority through third-party validation.' },
      { step: '05', title: 'Growth & Reputation Monitoring', desc: 'We track your brand mentions, manage your online reputation proactively, and refine strategy monthly to accelerate your influence.' },
    ],
    stats: [
      { value: '7x', label: 'More Leads for Personal Brands' },
      { value: '10k+', label: 'Avg. Monthly Reach Increase' },
      { value: '50+', label: 'Executives Managed' },
      { value: '3 Mo', label: 'To See Measurable Results' },
    ],
    platforms: ['LinkedIn', 'X (Twitter)', 'Instagram', 'YouTube', 'Podcasts', 'Online Publications', 'Newsletter / Substack', 'Speaking Events'],
    pricing: [
      {
        tier: 'Executive', price: 'X,XXX', period: '/month',
        description: 'LinkedIn-focused growth for professionals.',
        features: ['Profile Optimization', '12 LinkedIn Posts/month', 'Basic Engagement Strategy', 'Monthly Analytics'],
        isPopular: false
      },
      {
        tier: 'Authority', price: 'XX,XXX', period: '/month',
        description: 'Multi-platform thought leadership and PR.',
        features: ['LinkedIn + X (Twitter)', '20 Posts/month', '1 Long-form Article/month', '1 Podcast Booking/month', 'Dedicated Ghostwriter'],
        isPopular: true
      },
      {
        tier: 'Icon', price: 'XX,XXX', period: '/month',
        description: 'Total digital dominance for industry leaders.',
        features: ['All Platforms + Newsletter', 'Daily Content Publishing', '3 Podcast Bookings/month', 'Media Features & PR Outreach', 'Video Content Direction'],
        isPopular: false
      }
    ]
  },
];
